import { useState, useEffect, useRef } from 'react';

interface Comment {
  id: number;
  username: string;
  text: string;
  created_at: string;
}

interface CommentSectionProps {
  ratingId: number;
  username: string;
  isAdmin?: boolean;
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr + 'Z').getTime();
  const diff = Math.max(0, now - then);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function CommentSection({ ratingId, username, isAdmin = false }: CommentSectionProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`/api/rating/${ratingId}/comments`)
      .then(res => res.json())
      .then(data => setComments(data))
      .catch(() => {});
  }, [ratingId]);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || !username || submitting) return;
    if (trimmed.length > 280) return;

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`/api/rating/${ratingId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, text: trimmed }),
      });

      if (res.status === 429) {
        const data = await res.json();
        setError(data.error || 'Slow down! Too many comments. Try again in a bit.');
        return;
      }

      if (!res.ok) throw new Error('Failed to post comment');

      const newComment = await res.json();
      setComments(prev => [...prev, newComment]);
      setText('');

      // Scroll to bottom
      setTimeout(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
      }, 50);
    } catch {
      setError('Failed to post comment. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    if (deleting) return;
    setDeleting(commentId);

    try {
      const session = localStorage.getItem('jointRaterSession');
      const parsed = session ? JSON.parse(session) : null;
      const adminKey = parsed?.adminKey || '';

      const res = await fetch(`/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, adminKey }),
      });

      if (!res.ok) throw new Error('Failed');
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {
      // Silently fail
    } finally {
      setDeleting(null);
    }
  };

  const canDelete = (comment: Comment) =>
    comment.username === username || isAdmin;

  return (
    <div className="bg-cannabis-800 rounded-2xl border border-cannabis-700/30 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-cannabis-700/30">
        <h3 className="text-cream font-semibold text-xs">
          Comments {comments.length > 0 && <span className="text-cream/35">({comments.length})</span>}
        </h3>
      </div>

      {/* Comment list */}
      <div
        ref={listRef}
        className="max-h-72 overflow-y-auto divide-y divide-cannabis-700/20"
      >
        {comments.length === 0 ? (
          <div className="px-3 py-5 text-center text-cream/25 text-xs">
            No comments yet. Be the first!
          </div>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="px-3 py-2 hover:bg-cannabis-700/15 transition group">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-cannabis-400 font-semibold text-xs truncate">
                      {comment.username}
                    </span>
                    <span className="text-cream/20 text-[10px] shrink-0">
                      {relativeTime(comment.created_at)}
                    </span>
                  </div>
                  <p className="text-cream/70 text-xs break-words leading-relaxed">
                    {comment.text}
                  </p>
                </div>
                {canDelete(comment) && (
                  <button
                    onClick={() => handleDelete(comment.id)}
                    disabled={deleting === comment.id}
                    className="text-red-400/0 group-hover:text-red-400/60 hover:!text-red-400 transition text-xs shrink-0 mt-0.5 disabled:opacity-30"
                    title="Delete comment"
                  >
                    {deleting === comment.id ? '...' : '\u2715'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input area */}
      {username ? (
        <div className="border-t border-cannabis-700/30 p-2.5 space-y-1.5">
          {error && (
            <div className="text-red-400 text-[11px] bg-red-400/10 px-2.5 py-1.5 rounded-lg">
              {error}
            </div>
          )}
          <div className="flex gap-1.5">
            <div className="flex-1 relative">
              <input
                type="text"
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
                placeholder="Drop a comment..."
                maxLength={280}
                className="w-full bg-cannabis-900 border border-cannabis-600/50 rounded-xl px-3 py-1.5 text-cream text-xs placeholder:text-cream/20 focus:outline-none focus:border-warm/40 pr-10"
              />
              <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] ${text.length > 260 ? 'text-red-400' : 'text-cream/15'}`}>
                {text.length}/280
              </span>
            </div>
            <button
              onClick={handleSubmit}
              disabled={!text.trim() || text.trim().length > 280 || submitting}
              className="bg-cannabis-600 hover:bg-cannabis-500 disabled:opacity-30 disabled:cursor-not-allowed text-cream text-xs font-medium px-3 py-1.5 rounded-xl transition shrink-0"
            >
              {submitting ? '...' : 'Send'}
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-cannabis-700/30 p-2.5 text-center text-cream/25 text-[11px]">
          Log in to comment
        </div>
      )}
    </div>
  );
}
